import type { PreferenceProposalChoice } from '@elara/domain/models/schemas/widget-schemas';

import { cn } from '@elara/client-core/lib/utils';

import { Button, Card, IconSizes, MaterialIcon } from '#ui';

import { FLOATING_CARD_CLASS, FLOATING_CARD_STYLE } from './floating-card.js';

export interface PreferenceProposalPromptProps {
  /** The durable fact Elara is offering to remember, e.g. "I am a vegetarian." */
  statement: string;
  /** 1-based position of this proposal in the queue. */
  position: number;
  /** Total proposals waiting for an answer, including this one. */
  total: number;
  /** The answer being saved, if any: that button shows a spinner and the other is disabled. */
  savingChoice?: PreferenceProposalChoice;
  /** Why the last save failed, shown inline so the guest can retry. */
  error?: string;
  /** Plays the drop-away exit after the last proposal is declined; the card is inert meanwhile. */
  isLeaving?: boolean;
  onDecide: (choice: PreferenceProposalChoice) => void;
  className?: string;
}

/** Drop-away exit (slide down + fade) both cards play before they're removed. */
const EXIT_TRANSITION_CLASS = 'transition-all duration-200 ease-in motion-reduce:transition-none';
const EXIT_CLASS = 'pointer-events-none translate-y-3 opacity-0';

/**
 * Asks the guest whether Elara should remember a fact for future trips. Shown above the chat composer,
 * one proposal at a time; the queue and persistence live in `PendingPreferenceProposals`.
 */
export function PreferenceProposalPrompt({
  statement,
  position,
  total,
  savingChoice,
  error,
  isLeaving = false,
  onDecide,
  className,
}: PreferenceProposalPromptProps) {
  return (
    <Card
      role="region"
      aria-label="Remember this for future trips?"
      className={cn(FLOATING_CARD_CLASS, EXIT_TRANSITION_CLASS, 'flex flex-col gap-1', isLeaving && EXIT_CLASS, className)}
      style={FLOATING_CARD_STYLE}
    >
      {/* One row from `sm` up; on phones the counter and actions drop to a second row so the statement
          gets the full width. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 sm:flex-1">
          {/* The fact being saved is what the guest is judging, so it leads; the question is just its label. */}
          <p className="text-xs text-muted-foreground">Remember for future trips?</p>
          <p className="text-base">{statement}</p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {total > 1 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {position} of {total}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
          <Button
            variant="icon"
            size="icon-sm"
            tooltip="No thanks"
            aria-label="No thanks"
            isBusy={savingChoice === 'no'}
            disabled={savingChoice !== undefined}
            onClick={() => onDecide('no')}
          >
            <MaterialIcon icon="close" size={IconSizes.sm} />
          </Button>
          <Button
            variant="icon-outline"
            size="icon-sm"
            tooltip="Remember"
            aria-label="Remember"
            isBusy={savingChoice === 'yes'}
            disabled={savingChoice !== undefined}
            onClick={() => onDecide('yes')}
          >
            <MaterialIcon icon="check" size={IconSizes.sm} />
          </Button>
          </div>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </Card>
  );
}

export interface PreferenceSavedNoticeProps {
  /** Plays the drop-away exit (slide down + fade) before the notice is removed. */
  isLeaving?: boolean;
  /** Height of the prompt it replaces, so the bar doesn't shrink when the notice swaps in. */
  minHeight?: number;
  className?: string;
}

/**
 * Brief confirmation shown in the prompt's place once the queue is answered and at least one fact was
 * remembered. `PendingPreferenceProposals` times it and removes it after the exit transition.
 */
export function PreferenceSavedNotice({ isLeaving = false, minHeight, className }: PreferenceSavedNoticeProps) {
  return (
    <Card
      role="status"
      className={cn(
        FLOATING_CARD_CLASS,
        EXIT_TRANSITION_CLASS,
        'flex items-center text-sm',
        isLeaving && EXIT_CLASS,
        className,
      )}
      style={{ ...FLOATING_CARD_STYLE, minHeight }}
    >
      Thanks. We’ll remember that.
    </Card>
  );
}
